import { useAskQuestion, useMyQuestions } from '@/api/hooks';
import {
  AskQuestionForm,
  ErrorText,
  HelpTip,
  LinkedDocumentList,
  PersonDisplay,
  QuestionStatusChip,
  formatDate,
  type QuestionDocumentRef,
  type QuestionView,
  type RoomDocument,
} from '@dataroom/shared';

export interface QuestionsPanelProps {
  roomId: string;
  /** The documents this buyer can see (the page's listing) — for "about a
   * document" and to open a linked document in the viewer. */
  documents: RoomDocument[];
  onOpen: (doc: RoomDocument) => void;
}

/**
 * The portal's Q&A (ADR-0039). In a sell-side room it is the buyer's PRIVATE
 * thread with the deal team: a buyer sees only their own questions and only
 * answers the intermediary has released; nothing here speaks of other buyers or
 * of how a question is handled behind the scenes.
 *
 * In a BUYER-RUN room (`mode: 'buy_side'`) the reader is a member of the buy-side
 * team (the buyer's accountant, attorney, lender…): questions go straight to the
 * seller, the whole team sees every question (with who asked) and the seller's
 * answers as soon as they are written.
 */
export function QuestionsPanel({ roomId, documents, onOpen }: QuestionsPanelProps) {
  const questions = useMyQuestions(roomId);
  const ask = useAskQuestion(roomId);
  const buySide = questions.data?.mode === 'buy_side';

  const byId = new Map(documents.map((d) => [d.id, d]));
  const openLinked = (ref: QuestionDocumentRef) => {
    const doc = byId.get(ref.id);
    if (doc) onOpen(doc);
  };

  const list = questions.data?.questions ?? [];
  const listLabel = buySide ? "Your team's questions" : 'Your questions';

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        {buySide
          ? 'Ask the seller about the business or a document. Your whole team sees every question and the seller’s answers here.'
          : 'Ask the deal team about the business or a document. Answers appear here.'}
      </p>
      <AskQuestionForm
        documents={documents}
        pending={ask.isPending}
        error={ask.error}
        onAsk={(question, onDone) => ask.mutate(question, { onSuccess: onDone })}
        hint={
          buySide
            ? 'The seller sees your firm’s name, not who on your team asked.'
            : 'Only you and the deal team see your questions.'
        }
        sentMessage={
          buySide
            ? 'Question sent to the seller. The answer will appear here.'
            : 'Question sent. You’ll see the answer here.'
        }
      />

      <section aria-label={listLabel} className="space-y-3">
        <div className="flex items-center gap-1">
          <h3 className="text-sm font-semibold">{listLabel}</h3>
          <HelpTip id="buyer.questions.status" />
        </div>
        {questions.isPending ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : questions.error ? (
          <ErrorText error={questions.error} />
        ) : list.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {buySide
              ? 'No one on your team has asked a question yet.'
              : 'You haven’t asked any questions yet.'}
          </p>
        ) : (
          <ul className="space-y-3">
            {list.map((q) => (
              <BuyerQuestionItem
                key={q.id}
                question={q}
                buySide={buySide}
                onOpenLinked={openLinked}
              />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function BuyerQuestionItem({
  question: q,
  buySide,
  onOpenLinked,
}: {
  question: QuestionView;
  buySide: boolean;
  onOpenLinked: (doc: QuestionDocumentRef) => void;
}) {
  return (
    <li className="space-y-2 rounded-md border p-3">
      <div className="flex flex-wrap items-center gap-2">
        {buySide && q.buyer ? (
          <PersonDisplay
            name={q.buyer.displayName}
            email={q.buyer.email}
            size="sm"
            fallback="A team member"
          />
        ) : null}
        <QuestionStatusChip status={q.status} />
        <span className="text-xs text-muted-foreground">Asked {formatDate(q.createdAt)}</span>
      </div>
      <p className="whitespace-pre-wrap break-words text-sm">{q.body}</p>
      {q.document || q.category ? (
        <p className="text-xs text-muted-foreground">
          About: {q.document ? q.document.name || 'Untitled document' : q.category?.name}
        </p>
      ) : null}
      {q.answer ? (
        <div className="space-y-2 border-t pt-2">
          <p className="text-xs font-semibold text-ink-2">
            {buySide ? 'Seller’s answer' : 'Answer'}
            {q.answer.answeredAt ? ` · ${formatDate(q.answer.answeredAt)}` : ''}
          </p>
          <p className="whitespace-pre-wrap break-words text-sm">{q.answer.body}</p>
          <LinkedDocumentList documents={q.answer.documents} onOpen={onOpenLinked} />
        </div>
      ) : q.status === 'open' ? (
        <p className="text-xs text-muted-foreground">
          {buySide ? 'Waiting for the seller’s answer.' : 'Waiting for an answer.'}
        </p>
      ) : null}
    </li>
  );
}
